"use client";

import { useEffect } from "react";
import Image from "next/image";

import { Button } from "@/components/ui/button";
import { KeyCombo } from "@/components/ui/kbd";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { useDesktopBridge } from "@/hooks/useDesktopShellWindow";
import { DRAG_REGION_CLASS, NO_DRAG_REGION_CLASS } from "@/lib/guide/desktop-shell";
import { cn } from "@/lib/utils";
import logo from "@/public/logo-orange.png";

// The desktop app's window at sign-in, setup and the debrief: a white panel
// on the transparent window, with the strip the rep drags it by, since the
// window has no frame of its own. During a call the teleprompter draws the
// whole window instead (GuideApp). Links that leave Guide open in the rep's
// browser: this window is Guide and nothing else.

// Pages that stay in the window: Guide itself and the sign-in. Any other
// page on the site (the debrief's results link) belongs in the browser,
// where the rest of Salesably is. Links to other sites and links that ask
// for a new window are the main process's to route (desktop/src/window.js).
const WINDOW_PATHS = new Set(["/guide", "/"]);

function useLinksOpenInBrowser(bridge) {
  useEffect(() => {
    if (!bridge) return undefined;
    const onClick = (event) => {
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank" || event.defaultPrevented) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || WINDOW_PATHS.has(url.pathname)) return;
      event.preventDefault();
      bridge.openExternal(url.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [bridge]);
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children the page, scrolling inside the panel.
 * @param {string} [props.className] layout for the scrolling area.
 */
export default function DesktopPanel({ children, className }) {
  const bridge = useDesktopBridge();
  const apple = useApplePlatform();
  useLinksOpenInBrowser(bridge);

  return (
    <div className="flex h-screen flex-col overflow-hidden rounded-2xl border border-line bg-white">
      <div
        className={cn(
          "flex h-10 shrink-0 select-none items-center gap-2 border-b border-line bg-surface-chrome-light px-3",
          DRAG_REGION_CLASS,
        )}
      >
        <Image src={logo} alt="" width={18} height={18} />
        <span className="text-xs font-semibold text-gray-700">Salesably Guide</span>
        <Button
          variant="ghost"
          size="xs"
          className={cn("ml-auto gap-2", NO_DRAG_REGION_CLASS)}
          title="Show Guide again with the same keys"
          onClick={() => bridge?.hide()}
        >
          Hide
          <KeyCombo keys={apple ? ["⌘", "⇧", "G"] : ["Ctrl", "Shift", "G"]} />
        </Button>
      </div>
      <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-6", className)}>{children}</div>
    </div>
  );
}
